import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MeetingProgress } from 'components/meeting/MeetingProgress';
import { useSkipIfCreated } from 'hooks/useSkipIfCreated';
import { AppScreen, Button, ButtonFooter, PageIntro, Section } from 'components/ds';
import styles from './MeetingCreate.module.css';

const STORAGE_KEY = 'meeting_create_data';

type MeetingType = 'DAY' | 'TRAVEL';

/* 아이콘은 전부 인라인 SVG (이모지 금지) */

const IconMug = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="4" y="8" width="12" height="11" rx="2" />
        <path d="M16 11h2a2 2 0 0 1 0 4h-2" />
    </svg>
);

const IconPlane = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polygon points="21 3 3 10 10 13 13 21 21 3" />
    </svg>
);

const IconCheckBold = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const FeatureCheck = () => (
    <svg className="feature-check-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const IconBulb = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 18h6" />
        <path d="M10 22h4" />
        <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
    </svg>
);

export const MeetingTypeSelect = () => {
    useSkipIfCreated();
    const navigate = useNavigate();

    useEffect(() => {
        // SharedPlace (외부 지도앱 공유 흐름) 에서 selectedPlace / selectedPlaceInfo 를 미리 set 한 경우 보존.
        // 그 외 일반 진입은 기존처럼 깨끗이 reset.
        const existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
        if (existing.selectedPlace || existing.selectedPlaceInfo) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                selectedPlace: existing.selectedPlace,
                selectedPlaceInfo: existing.selectedPlaceInfo,
            }));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const { t } = useTranslation();
    const [selectedType, setSelectedType] = useState<MeetingType>('DAY');

    const handleNext = () => {
        if (selectedType) {
            // 기존 sessionStorage 값(특히 SharedPlace 가 set 한 선택 장소) 유지하면서 meetingType 만 추가
            const existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
                ...existing,
                meetingType: selectedType,
            }));
            navigate('/meeting/create/step2');
        }
    };

    const types = [
        {
            id: 'DAY' as const,
            Icon: IconMug,
            title: t('meetingCreate.dayTitle'),
            desc: t('meetingCreate.dayDesc'),
            features: [t('createFlow.featDateTime'), t('createFlow.featPlaceShare'), t('createFlow.featSettle')],
        },
        {
            id: 'TRAVEL' as const,
            Icon: IconPlane,
            title: t('meetingCreate.travelTitle'),
            desc: t('meetingCreate.travelDesc'),
            features: [
                t('createFlow.featTravelPeriod'),
                t('createFlow.featDailyPlan'),
                t('createFlow.featAiPlan'),
                t('createFlow.featHotelTransit'),
            ],
        },
    ];

    return (
        <AppScreen title={t('meetingCreate.title')} bar="sub" hasFooter>
            <MeetingProgress currentStep={1} isTravel={selectedType === 'TRAVEL'} />

            <PageIntro
                title={t('meetingCreate.step1Title')}
                desc={t('meetingCreate.step1Subtitle')}
            />

            <Section spacing="tight">
                <div className={styles.picks}>
                    {types.map(ty => {
                        const on = selectedType === ty.id;
                        return (
                            <button
                                key={ty.id}
                                type="button"
                                className={on ? `${styles.pick} ${styles.pickOn}` : styles.pick}
                                onClick={() => setSelectedType(ty.id)}
                                aria-pressed={on}
                            >
                                <span className={styles.pickIcon}><ty.Icon /></span>
                                <span className={styles.pickBody}>
                                    <span className={styles.pickTitle}>{ty.title}</span>
                                    <span className={styles.pickDesc}>{ty.desc}</span>
                                    <span className={styles.pickFeatures}>
                                        {ty.features.map(f => (
                                            <span key={f} className={styles.feature}>{f}</span>
                                        ))}
                                    </span>
                                </span>
                                <span className={styles.pickMark} aria-hidden>
                                    {on && <IconCheckBold />}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </Section>

            <Section divided last>
                <p className={styles.info}>
                    {selectedType === 'TRAVEL'
                        ? t('createFlow.mtsTravelDesc')
                        : t('createFlow.mtsDayDesc')}
                </p>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={!selectedType}
                    onClick={handleNext}
                >
                    {t('common.next')}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};
